/**
 * Installs a document-level copy event listener that strips dark background
 * colours from clipboard HTML. Without this, copying text from dark-surfaced
 * areas (report preview, Trova Compass, Deep Search) and pasting into Word or
 * Google Docs produces text with a black/dark background inherited from the
 * app's dark theme.
 *
 * Returns a cleanup function that removes the listener (call it on unmount).
 */
export function installClipboardCleaner(): () => void {
  const handler = (event: ClipboardEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !event.clipboardData) return;

    // Build a temporary container holding a clone of the selected HTML
    const range = selection.getRangeAt(0);
    const fragment = range.cloneContents();
    const container = document.createElement('div');
    container.appendChild(fragment);

    // Strip dark backgrounds (and very-dark inline text colours) from every element
    stripDarkStyles(container);

    // Write the cleaned HTML + plain text to the clipboard in place of the default
    const cleanedHtml = container.innerHTML;
    const plainText = (container as HTMLElement).innerText ?? container.textContent ?? '';

    event.preventDefault();
    event.clipboardData.setData('text/html', cleanedHtml);
    event.clipboardData.setData('text/plain', plainText);
  };

  document.addEventListener('copy', handler);
  return () => document.removeEventListener('copy', handler);
}

/**
 * Recursively removes background-color / background inline styles from an
 * element and its descendants so the pasted content picks up the target
 * document's background (white in Word / Google Docs) rather than inheriting
 * the app's dark theme.
 */
function stripDarkStyles(el: Element): void {
  if (el instanceof HTMLElement) {
    el.style.removeProperty('background-color');
    el.style.removeProperty('background');

    // If an inline colour is very dark it would be invisible on a white paste
    // target — clear it so Word/Docs applies its own default text colour.
    const inlineColor = el.style.color;
    if (inlineColor && isDarkColor(inlineColor)) {
      el.style.removeProperty('color');
    }
  }

  for (let i = 0; i < el.children.length; i++) {
    stripDarkStyles(el.children[i]);
  }
}

/**
 * Returns true for colours that would be unreadable on a white paste target.
 * Only examines rgb() / rgba() values — named colours and CSS vars are skipped.
 */
function isDarkColor(color: string): boolean {
  const rgb = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!rgb) return false;
  const r = Number(rgb[1]);
  const g = Number(rgb[2]);
  const b = Number(rgb[3]);
  // Perceived luminance (WCAG formula) — values below 50 are "dark"
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 50;
}
